var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { FormControlMixin } from '@open-wc/form-control';
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { reset } from '../styles';
import { styles as hostStyles } from './host-styles';
import { styles as radioStyles } from './radio-styles';
/**
 * @parent w-radio-group
 */
export class WarpRadio extends FormControlMixin(LitElement) {
    static { this.styles = [hostStyles, reset, radioStyles]; }
    /** @internal */
    static { this.shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    }; }
    /**
     * Override tabIndex getter to return the computed internal tabIndex.
     * This allows external code to check if the radio is focusable.
     * @internal
     */
    get tabIndex() {
        return this._internalTabIndex;
    }
    /**
     * Override tabIndex setter to set _groupTabIndex (for backwards compatibility).
     * Radio-group should use _groupTabIndex directly for clarity.
     *
     * @internal
     */
    set tabIndex(value) {
        this._groupTabIndex = value;
    }
    /** The default value of the form control. Used for resetting. */
    #defaultChecked;
    // Track whether invalid state was set by required validation.
    #invalidFromRequired;
    // Track whether the user has interacted with the radio.
    #hasInteracted;
    constructor() {
        super();
        /**
         * The radio's value. When selected, the radio group will receive this value.
         */
        this.value = null;
        /**
         * Draws the radio in a checked state (reflected to attribute).
         */
        this.checked = false;
        /**
         * Disables the radio.
         */
        this.disabled = false;
        /**
         * Makes the radio a required field.
         */
        this.required = false;
        /**
         * Draws the radio in an invalid state.
         */
        this.invalid = false;
        /** The default value of the form control. Used for resetting. */
        this.#defaultChecked = false;
        // Track whether invalid state was set by required validation.
        this.#invalidFromRequired = false;
        // Track whether the user has interacted with the radio.
        this.#hasInteracted = false;
        this.handleClick = () => {
            if (this.isInGroup())
                return;
            if (this.disabled)
                return;
            this.#hasInteracted = true;
            if (this.checked)
                return;
            this.checked = true;
            this.updateComplete.then(() => {
                this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
            });
        };
        this.handleInvalid = () => {
            // invalid fires for failed validation attempts (submit/reportValidity), so treat it as interaction.
            this.#hasInteracted = true;
            this.updateValidity();
        };
        this.handleKeyDown = (event) => {
            if (this.isInGroup())
                return;
            if (this.disabled)
                return;
            if (event.defaultPrevented)
                return;
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
                const radios = this.getStandaloneNamedRadios().filter((radio) => !radio.disabled);
                if (radios.length <= 1)
                    return;
                event.preventDefault();
                const current = radios.find((radio) => radio.checked) ?? radios.find((radio) => radio === this) ?? radios[0];
                const direction = event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;
                const currentIndex = radios.indexOf(current);
                const nextIndex = (currentIndex + direction + radios.length) % radios.length;
                const nextRadio = radios[nextIndex];
                nextRadio.#hasInteracted = true;
                if (!nextRadio.checked) {
                    nextRadio.checked = true;
                    nextRadio.updateComplete.then(() => {
                        nextRadio.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
                    });
                }
                nextRadio.focus();
                return;
            }
            if (event.key !== ' ' && event.key !== 'Spacebar' && event.key !== 'Enter')
                return;
            if (event.key === 'Enter' && this.internals.form) {
                this.internals.form.requestSubmit();
                return;
            }
            event.preventDefault();
            this.click();
        };
        this.addEventListener('click', this.handleClick);
        this.addEventListener('invalid', this.handleInvalid);
        this.addEventListener('keydown', this.handleKeyDown);
    }
    connectedCallback() {
        super.connectedCallback();
        this.value = this.getAttribute('value') ?? 'on';
        this.#defaultChecked = this.hasAttribute('checked');
        this.checked = this.#defaultChecked;
        // Use ElementInternals for ARIA to avoid hydration mismatches
        this.internals.role = 'radio';
        this.syncAriaDisabled();
        this.syncFormValue();
        this.updateValidity();
    }
    syncAriaDisabled() {
        this.internals.ariaDisabled = this.disabled ? 'true' : 'false';
    }
    syncAriaChecked() {
        this.internals.ariaChecked = this.checked ? 'true' : 'false';
    }
    willUpdate(changedProperties) {
        if (this.shouldSyncFormState(changedProperties)) {
            this.syncFormValue();
            this.updateValidity();
        }
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('checked')) {
            this.syncAriaChecked();
            // Uncheck other radios immediately (functional behavior)
            if (this.checked && !this.isInGroup()) {
                this.uncheckOtherRadios();
                this.syncStandaloneTabOrder();
            }
        }
        if (changedProperties.has('disabled')) {
            this.syncAriaDisabled();
            if (!this.isInGroup()) {
                this.syncStandaloneTabOrder();
            }
        }
        if (changedProperties.has('invalid')) {
            this.internals.ariaInvalid = this.invalid ? 'true' : null;
        }
        if (changedProperties.has('name')) {
            if (this.checked && !this.isInGroup()) {
                this.uncheckOtherRadios();
            }
        }
    }
    /** @internal */
    resetFormControl() {
        this.checked = this.#defaultChecked;
        this.syncFormValue();
        this.updateValidity();
    }
    /**
     * Returns the validation message if the radio is invalid, otherwise an empty string
     * @internal
     */
    get validationMessage() {
        return this.internals.validationMessage;
    }
    /**
     * Returns the validity state of the radio
     * @internal
     */
    get validity() {
        return this.internals.validity;
    }
    /**
     * Checks whether the radio passes constraint validation
     * @internal
     */
    checkValidity() {
        this.updateValidity();
        return this.internals.checkValidity();
    }
    /**
     * Checks validity and shows the browser's validation message if invalid
     * @internal
     */
    reportValidity() {
        // reportValidity is an explicit request to surface validation feedback.
        this.#hasInteracted = true;
        this.updateValidity();
        return this.internals.checkValidity();
    }
    isInGroup() {
        return Boolean(this.closest('w-radio-group'));
    }
    // The scope for radios is their containing form, or the document if they're not in a form. This is where we look for
    // other radios with the same name to uncheck when this radio is checked.
    getRadioScope() {
        return this.internals.form ?? this.closest('form') ?? document;
    }
    getStandaloneNamedRadios() {
        if (!this.name)
            return [this];
        const scope = this.getRadioScope();
        return Array.from(scope.querySelectorAll(`w-radio[name="${this.name}"]`)).filter((radio) => !radio.closest('w-radio-group'));
    }
    syncStandaloneTabOrder() {
        const radios = this.getStandaloneNamedRadios();
        const enabledRadios = radios.filter((radio) => !radio.disabled);
        const checkedRadio = enabledRadios.find((radio) => radio.checked);
        const activeRadio = checkedRadio ?? enabledRadios[0] ?? null;
        radios.forEach((radio) => {
            radio._standaloneTabIndex = radio === activeRadio ? 0 : -1;
        });
    }
    /**
     * Computed tabindex for the internal focusable element.
     * Priority: group-managed > standalone-managed > default
     */
    get _internalTabIndex() {
        if (this.disabled)
            return -1;
        if (this._groupTabIndex !== undefined)
            return this._groupTabIndex;
        if (this._standaloneTabIndex !== undefined)
            return this._standaloneTabIndex;
        // Default: first radio in standalone group is focusable
        return 0;
    }
    firstUpdated() {
        // Initialize standalone tab order if not in a group
        if (!this.isInGroup()) {
            this.syncStandaloneTabOrder();
        }
    }
    uncheckOtherRadios() {
        if (!this.name)
            return;
        const scope = this.getRadioScope();
        const radios = Array.from(scope.querySelectorAll(`w-radio[name="${this.name}"]`));
        radios.forEach((radio) => {
            if (radio === this)
                return;
            if (radio.closest('w-radio-group'))
                return;
            if (radio.checked) {
                radio.checked = false;
            }
        });
    }
    updateValidity() {
        if (this.disabled || this.isInGroup()) {
            this.internals.setValidity({});
            return;
        }
        const requiredInvalid = this.required && !this.checked;
        // this means that invalid was set via the invalid attribute, not by required validation
        const externalInvalid = this.invalid && !this.#invalidFromRequired;
        if (requiredInvalid) {
            this.#invalidFromRequired = true;
            this.invalid = this.#hasInteracted;
            // Use a non-empty message to avoid native popovers while satisfying ElementInternals.
            this.internals.setValidity({ valueMissing: true }, this.internals.validationMessage || ' ');
            return;
        }
        if (this.#invalidFromRequired) {
            this.invalid = false;
            this.#invalidFromRequired = false;
        }
        if (externalInvalid) {
            this.internals.setValidity({ customError: true }, this.internals.validationMessage || ' ');
            return;
        }
        this.internals.setValidity({});
    }
    syncFormValue() {
        if (this.disabled) {
            this.setValue(null);
            return;
        }
        this.setValue(this.checked ? this.value : null);
    }
    shouldSyncFormState(changedProperties) {
        return (changedProperties.has('checked') ||
            changedProperties.has('value') ||
            changedProperties.has('disabled') ||
            changedProperties.has('required') ||
            changedProperties.has('invalid'));
    }
    render() {
        return html `
      <div class="wrapper" tabindex="${this._internalTabIndex}">
        <div part="control" class="control"></div>
        <slot part="label" class="label"></slot>
      </div>
    `;
    }
}
__decorate([
    property({ reflect: true })
], WarpRadio.prototype, "name", void 0);
__decorate([
    property({ reflect: true })
], WarpRadio.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpRadio.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpRadio.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpRadio.prototype, "required", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpRadio.prototype, "invalid", void 0);
__decorate([
    property({ attribute: false })
], WarpRadio.prototype, "_groupTabIndex", void 0);
__decorate([
    property({ attribute: false })
], WarpRadio.prototype, "_standaloneTabIndex", void 0);
/**
 * @deprecated Exported for backwards compatibility only, use `WarpRadio`
 */
export const WRadio = WarpRadio;
if (!customElements.get('w-radio')) {
    customElements.define('w-radio', WarpRadio);
}
//# sourceMappingURL=radio.js.map